import React, { useState, useCallback, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';

import { DateTime } from 'luxon';

import { Breadcrumb } from '@shared/components/bootstrap';
import { Text } from '@shared/components/bootstrap';
import { Pagination } from '@shared/components/pagination/pagination';
import { useDebounce } from '@shared/hooks';
import { useURLSearchParam } from '@shared/hooks/use_url_search_param';
import { IPagination } from '@shared/types/pagination';

import { Panel } from '@admin/components/helpers/panel';
import { Permission } from '@admin/components/helpers/permission';
import { Spinner } from '@admin/components/spinner';
import { warehouseTaskURL, warehouseTasksURL } from '@admin/config/routes';
import { useTitle } from '@admin/hooks/use_title';
import { WAREHOUSE_OPS } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import {
  AccountItemsQuery,
  AccountsQuery,
  WarehouseTask__ActionType,
  useWarehouseTaskCreateMutation,
  useAccountsQuery,
  useAccountItemsQuery,
} from '@admin/schema';

import { ACTION_TYPE_LABELS, ACTION_GROUP_LABELS, ACTIONS_BY_GROUP } from './constants';

const REQUIRED_STAR = <span style={{ color: '#d9534f' }}> *</span>;

const TEAL = '#17a2b8';
const TEAL_BG = '#e8f7f9';
const TEAL_BORDER = '#b8e8ee';

interface ItemAction {
  actionType: WarehouseTask__ActionType;
  notes: string;
}

interface SelectedItemEntry {
  itemId: string;
  actions: ItemAction[];
}

interface SelectedAccount {
  id: string;
  number: string;
  customerName: string;
}

type AccountLookupMode = 'search' | 'id';
type AccountFromAccountsQuery = NonNullable<AccountsQuery['paginated']['results'][number]>;

export const WarehouseTaskCreateContent: React.FC = () => {
  useTitle('Warehouse Task - New');

  const [accountID] = useURLSearchParam('accountID');

  const [accountQuery, setAccountQuery] = useState('');
  const [accountLookupMode, setAccountLookupMode] = useState<AccountLookupMode>('search');
  const [accountIdInput, setAccountIdInput] = useState('');
  const [selectedAccount, setSelectedAccount] = useState<SelectedAccount | null>(() => {
    if (!accountID) return null;
    return { id: accountID, number: `#${accountID}`, customerName: `Account ${accountID}` };
  });

  const [initialComment, setInitialComment] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [selectedItems, setSelectedItems] = useState<Map<string, SelectedItemEntry>>(new Map());
  const [expandedItemIds, setExpandedItemIds] = useState<Set<string>>(new Set());
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [itemBrowsePage, setItemBrowsePage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [createTask] = useWarehouseTaskCreateMutation({ client });

  const debouncedAccountQuery = useDebounce(accountQuery);
  const { data: accountsData, loading: accountsLoading } = useAccountsQuery({
    client,
    fetchPolicy: 'no-cache',
    variables: { query: debouncedAccountQuery },
    skip: !debouncedAccountQuery || debouncedAccountQuery.length < 3 || !!selectedAccount,
  });

  const debouncedItemSearch = useDebounce(itemSearchQuery);
  const { data: accountItemsData, loading: accountItemsLoading } = useAccountItemsQuery({
    client,
    variables: {
      accountID: selectedAccount?.id || '',
      page: itemBrowsePage,
      per: 25,
      search: debouncedItemSearch || undefined,
    },
    skip: !selectedAccount,
    fetchPolicy: 'cache-and-network',
  });

  const handleAccountSelect = (account: AccountFromAccountsQuery) => {
    setSelectedAccount({ id: account.id, number: account.number, customerName: account.customer.name });
    setAccountQuery('');
    setSelectedItems(new Map());
    setItemSearchQuery('');
    setItemBrowsePage(1);
    setExpandedItemIds(new Set());
  };

  const handleAccountIdSubmit = () => {
    const trimmed = accountIdInput.trim();
    if (!trimmed) return;
    setSelectedAccount({ id: trimmed, number: `#${trimmed}`, customerName: `Account ${trimmed}` });
    setSelectedItems(new Map());
    setItemSearchQuery('');
    setItemBrowsePage(1);
    setExpandedItemIds(new Set());
  };

  const handleClearAccount = () => {
    setSelectedAccount(null);
    setAccountQuery('');
    setAccountIdInput('');
    setSelectedItems(new Map());
    setItemSearchQuery('');
    setItemBrowsePage(1);
    setExpandedItemIds(new Set());
  };

  const toggleItemSelection = useCallback((itemId: string) => {
    setSelectedItems((prev) => {
      const next = new Map(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
        setExpandedItemIds((prev) => {
          const nextSet = new Set(prev);
          nextSet.delete(itemId);
          return nextSet;
        });
      } else {
        next.set(itemId, { itemId, actions: [] });
        setExpandedItemIds((prev) => new Set(prev).add(itemId));
      }
      return next;
    });
  }, []);

  const toggleAction = useCallback((itemId: string, actionType: WarehouseTask__ActionType) => {
    setSelectedItems((prev) => {
      const next = new Map(prev);
      const entry = next.get(itemId);
      if (!entry) return prev;
      const hasAction = entry.actions.some((a) => a.actionType === actionType);
      const updatedActions = hasAction
        ? entry.actions.filter((a) => a.actionType !== actionType)
        : [...entry.actions, { actionType, notes: '' }];
      next.set(itemId, { ...entry, actions: updatedActions });
      return next;
    });
  }, []);

  const setActionNotes = useCallback((itemId: string, actionType: WarehouseTask__ActionType, notes: string) => {
    setSelectedItems((prev) => {
      const next = new Map(prev);
      const entry = next.get(itemId);
      if (!entry) return prev;
      const updatedActions = entry.actions.map((a) => (a.actionType === actionType ? { ...a, notes } : a));
      next.set(itemId, { ...entry, actions: updatedActions });
      return next;
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!selectedAccount || !dueDate) {
      setError('Please fill in all required fields');
      return;
    }

    if (selectedItems.size === 0) {
      setError('Please select at least one item');
      return;
    }

    const itemsWithoutActions = Array.from(selectedItems.values()).filter((entry) => entry.actions.length === 0);
    if (itemsWithoutActions.length > 0) {
      setError('Each selected item must have at least one action assigned');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const parsed = DateTime.fromISO(dueDate);
      if (!parsed.isValid) {
        setError('Invalid due date');
        setSubmitting(false);
        return;
      }
      const isoDueDate = parsed.toISO({ suppressMilliseconds: true })!;

      const result = await createTask({
        variables: {
          input: {
            accountID: selectedAccount.id,
            initialComment: initialComment.trim() || undefined,
            dueDate: isoDueDate,
            items: Array.from(selectedItems.values()).map((entry) => ({
              itemID: entry.itemId,
              actions: entry.actions.map((a) => {
                const trimmedNotes = a.notes?.trim();
                return {
                  actionType: a.actionType,
                  notes: trimmedNotes || undefined,
                };
              }),
            })),
          },
        },
      });

      if (result.data?.warehouseTaskCreate.status === 'OK' && result.data.warehouseTaskCreate.warehouseTask) {
        window.location.href = warehouseTaskURL({ id: result.data.warehouseTaskCreate.warehouseTask.id });
      } else {
        setError(result.data?.warehouseTaskCreate.error || 'Failed to create task');
      }
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const accounts = accountsData?.paginated.results;
  const showAccountResults = !selectedAccount && debouncedAccountQuery && debouncedAccountQuery.length >= 3;
  const accountItems = accountItemsData?.accountItems;

  return (
    <div>
      <div className="page-header text-overflow">
        <Breadcrumb>
          <Breadcrumb.Item>
            <NavLink to={warehouseTasksURL()}>Warehouse Tasks</NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item active>New Task</Breadcrumb.Item>
        </Breadcrumb>
      </div>

      <Panel>
        <Panel.Header>
          <Panel.Title>Create Warehouse Task</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Account{REQUIRED_STAR}</label>
              {selectedAccount ? (
                <div
                  className="well well-sm"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <span>
                    <strong>{selectedAccount.customerName}</strong>{' '}
                    <span className="text-muted">({selectedAccount.number})</span>
                  </span>
                  <button type="button" className="btn btn-xs btn-default" onClick={handleClearAccount}>
                    Change
                  </button>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: 8 }}>
                    <div className="btn-group btn-group-xs">
                      <button
                        type="button"
                        className={`btn ${accountLookupMode === 'search' ? 'btn-primary' : 'btn-default'}`}
                        onClick={() => setAccountLookupMode('search')}
                      >
                        Search
                      </button>
                      <button
                        type="button"
                        className={`btn ${accountLookupMode === 'id' ? 'btn-primary' : 'btn-default'}`}
                        onClick={() => setAccountLookupMode('id')}
                      >
                        Enter ID
                      </button>
                    </div>
                  </div>
                  {accountLookupMode === 'search' ? (
                    <>
                      <input
                        type="text"
                        className="form-control"
                        value={accountQuery}
                        onChange={(e) => setAccountQuery(e.target.value)}
                        placeholder="Search by name, email, or phone..."
                        aria-label="Search accounts by name, email, or phone"
                      />
                      {accountsLoading && <Spinner />}
                      {showAccountResults && accounts && accounts.length > 0 && (
                        <table className="table table-hover" style={{ marginTop: 4 }}>
                          <thead>
                            <tr>
                              <th>Account #</th>
                              <th>Customer</th>
                              <th>Phone</th>
                            </tr>
                          </thead>
                          <tbody>
                            {accounts.map((account) => (
                              <tr
                                key={account.id}
                                style={{ cursor: 'pointer' }}
                                tabIndex={0}
                                role="button"
                                onClick={() => handleAccountSelect(account)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleAccountSelect(account);
                                  }
                                }}
                              >
                                <td>{account.number}</td>
                                <td>{account.customer.name}</td>
                                <td>{account.customer.phone}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      {showAccountResults && accounts && accounts.length === 0 && !accountsLoading && (
                        <p className="text-muted" style={{ marginTop: 8 }}>
                          No accounts found
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        value={accountIdInput}
                        onChange={(e) => setAccountIdInput(e.target.value)}
                        placeholder="Enter account ID..."
                        aria-label="Account ID"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAccountIdSubmit();
                          }
                        }}
                      />
                      <span className="input-group-btn">
                        <button
                          type="button"
                          className="btn btn-default"
                          onClick={handleAccountIdSubmit}
                          disabled={!accountIdInput.trim()}
                        >
                          Select
                        </button>
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="due-date-input">Due Date{REQUIRED_STAR}</label>
              <input
                id="due-date-input"
                type="datetime-local"
                className="form-control"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>

            {selectedAccount && (
              <div className="form-group">
                <label>
                  Items & Actions{REQUIRED_STAR}
                  {selectedItems.size > 0 && (
                    <span style={{ marginLeft: 8, color: TEAL, fontWeight: 400, fontSize: 13 }}>
                      {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected
                    </span>
                  )}
                </label>
                <ItemBrowser
                  items={accountItems?.results}
                  pagination={accountItems?.pagination}
                  loading={accountItemsLoading}
                  selectedItems={selectedItems}
                  expandedItemIds={expandedItemIds}
                  searchQuery={itemSearchQuery}
                  onSearchChange={(query) => {
                    setItemSearchQuery(query);
                    setItemBrowsePage(1);
                  }}
                  onToggle={toggleItemSelection}
                  onToggleExpand={(itemId) =>
                    setExpandedItemIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(itemId)) next.delete(itemId);
                      else next.add(itemId);
                      return next;
                    })
                  }
                  onToggleAction={toggleAction}
                  onSetNotes={setActionNotes}
                  onPage={setItemBrowsePage}
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="initial-comment-textarea">
                Initial Comment{' '}
                <span className="text-muted" style={{ fontWeight: 400 }}>
                  (optional)
                </span>
              </label>
              <textarea
                id="initial-comment-textarea"
                className="form-control"
                rows={2}
                value={initialComment}
                onChange={(e) => setInitialComment(e.target.value)}
                placeholder="Leave a comment for the warehouse team..."
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={submitting || !selectedAccount}>
              {submitting ? (
                <>
                  <span
                    className="glyphicon glyphicon-refresh"
                    style={{ marginRight: 6, animation: 'spin 1s linear infinite' }}
                  />
                  Creating Task...
                </>
              ) : (
                'Create Task'
              )}
            </button>
            {submitting && (
              <span className="text-muted" style={{ marginLeft: 12, fontSize: 13 }}>
                Saving your task, please wait...
              </span>
            )}
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          </form>
        </Panel.Body>
      </Panel>
    </div>
  );
};

export const WarehouseTaskCreate: React.FC = () => (
  <Permission
    resource={WAREHOUSE_OPS.resource}
    action={WAREHOUSE_OPS.actions.create}
    denied={
      <Panel>
        <Panel.Body>
          <Text tag="p" style="muted">
            You do not have permission to create warehouse tasks.
          </Text>
        </Panel.Body>
      </Panel>
    }
  >
    <WarehouseTaskCreateContent />
  </Permission>
);

type AccountItem = NonNullable<AccountItemsQuery['accountItems']>['results'][number];

const STATE_DISPLAY: Record<string, { label: string; className: string }> = {
  STORED: { label: 'Stored', className: 'label-primary' },
  PACKED: { label: 'Packed', className: 'label-primary' },
  PULLED: { label: 'Pulled', className: 'label-primary' },
  PREPARED: { label: 'Prepared', className: 'label-primary' },
  ENROUTE: { label: 'Enroute', className: 'label-primary' },
  ON_TRAILER: { label: 'On Trailer', className: 'label-primary' },
  FLAGGED: { label: 'Flagged', className: 'label-danger' },
  LOST: { label: 'Lost', className: 'label-danger' },
  RETURNED: { label: 'Returned', className: 'label-default' },
  AUCTIONED: { label: 'Auctioned', className: 'label-default' },
  DISPOSED: { label: 'Disposed', className: 'label-default' },
};

const getStateDisplay = (state: string) =>
  STATE_DISPLAY[state] || { label: state.replace(/_/g, ' ').toLowerCase(), className: 'label-default' };

interface ItemBrowserProps {
  items?: AccountItem[];
  pagination?: IPagination;
  loading: boolean;
  selectedItems: Map<string, SelectedItemEntry>;
  expandedItemIds: Set<string>;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onToggle: (id: string) => void;
  onToggleExpand: (id: string) => void;
  onToggleAction: (itemId: string, actionType: WarehouseTask__ActionType) => void;
  onSetNotes: (itemId: string, actionType: WarehouseTask__ActionType, notes: string) => void;
  onPage: (page: number) => void;
}

const ItemBrowser: React.FC<ItemBrowserProps> = ({
  items,
  pagination,
  loading,
  selectedItems,
  expandedItemIds,
  searchQuery,
  onSearchChange,
  onToggle,
  onToggleExpand,
  onToggleAction,
  onSetNotes,
  onPage,
}) => (
  <div style={{ border: '1px solid #ddd', borderRadius: 4, maxHeight: 600, overflow: 'auto' }}>
    <div style={{ padding: 8, borderBottom: '1px solid #eee', background: '#fafafa' }}>
      <input
        type="text"
        className="form-control input-sm"
        placeholder="Search items by name, contents, categories, ID, or barcode..."
        aria-label="Search items by name, contents, categories, ID, or barcode"
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
      />
    </div>
    {loading && !items ? (
      <div style={{ padding: 24 }}>
        <Spinner />
      </div>
    ) : items && items.length > 0 ? (
      <>
        <table className="table table-condensed" style={{ marginBottom: 0 }}>
          <thead>
            <tr style={{ background: '#f5f5f5' }}>
              <th style={{ width: 30, paddingLeft: 12 }} />
              <th style={{ width: 40 }} />
              <th>Name</th>
              <th>Barcode</th>
              <th>Category</th>
              <th>State</th>
              <th>Warehouse</th>
              <th style={{ width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const selected = selectedItems.has(item.id);
              const expanded = expandedItemIds.has(item.id) && selected;
              const entry = selectedItems.get(item.id);
              return (
                <ItemRow
                  key={item.id}
                  item={item}
                  selected={selected}
                  expanded={expanded}
                  entry={entry}
                  onToggle={onToggle}
                  onToggleExpand={onToggleExpand}
                  onToggleAction={onToggleAction}
                  onSetNotes={onSetNotes}
                />
              );
            })}
          </tbody>
        </table>
        {pagination && (
          <div style={{ padding: 8, borderTop: '1px solid #eee' }}>
            <Pagination pagination={pagination} onPage={onPage} />
          </div>
        )}
      </>
    ) : (
      <p className="text-muted text-center" style={{ margin: 24 }}>
        No items found on this account
      </p>
    )}
  </div>
);

const ItemRow: React.FC<{
  item: AccountItem;
  selected: boolean;
  expanded: boolean;
  entry?: SelectedItemEntry;
  onToggle: (id: string) => void;
  onToggleExpand: (id: string) => void;
  onToggleAction: (itemId: string, actionType: WarehouseTask__ActionType) => void;
  onSetNotes: (itemId: string, actionType: WarehouseTask__ActionType, notes: string) => void;
}> = ({ item, selected, expanded, entry, onToggle, onToggleExpand, onToggleAction, onSetNotes }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [previewPos, setPreviewPos] = useState({ x: 0, y: 0 });
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const heroImage = item.images[item.hero] || item.images[0];
  const thumbnailSrc = heroImage?.thumbnailURL;

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (!thumbnailSrc) return;
    clearTimeout(timeoutRef.current);
    setPreviewPos({ x: e.clientX, y: e.clientY });
    setShowPreview(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (showPreview) setPreviewPos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setShowPreview(false), 100);
  };

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const actionCount = entry?.actions.length ?? 0;
  const stateDisplay = getStateDisplay(item.state);

  return (
    <>
      <tr
        style={{
          cursor: 'pointer',
          background: selected ? TEAL_BG : undefined,
          borderLeft: selected ? `3px solid ${TEAL}` : '3px solid transparent',
        }}
        onClick={() => {
          if (!selected) {
            onToggle(item.id);
          } else {
            onToggleExpand(item.id);
          }
        }}
      >
        <td
          style={{ paddingLeft: 12 }}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(item.id);
          }}
        >
          <input
            type="checkbox"
            checked={selected}
            readOnly
            aria-label={`Select item ${item.name || item.id}`}
            style={{ accentColor: TEAL }}
          />
        </td>
        <td
          onMouseEnter={handleMouseEnter}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          style={{ position: 'relative' }}
        >
          {thumbnailSrc ? (
            <>
              <img src={thumbnailSrc} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 3 }} />
              {showPreview && (
                <div
                  style={{
                    position: 'fixed',
                    left: previewPos.x + 16,
                    top: previewPos.y - 100,
                    zIndex: 9999,
                    background: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    padding: 4,
                    pointerEvents: 'none',
                  }}
                >
                  <img
                    src={thumbnailSrc}
                    alt={item.name || ''}
                    style={{ maxWidth: 240, maxHeight: 240, display: 'block' }}
                  />
                </div>
              )}
            </>
          ) : (
            <div
              style={{
                width: 32,
                height: 32,
                background: '#eee',
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#bbb',
                fontSize: 10,
              }}
            >
              --
            </div>
          )}
        </td>
        <td style={{ fontWeight: selected ? 600 : 400 }}>{item.name || item.contents || '-'}</td>
        <td>
          <code style={{ fontSize: 11 }}>{item.barcode?.value || '-'}</code>
        </td>
        <td>
          <div>{item.category?.name || '-'}</div>
          {item.group && (
            <div className="text-muted" style={{ fontSize: 11, marginTop: 2, lineHeight: 1.3 }}>
              {item.group.category?.name ? `Part of group · ${item.group.category.name}` : 'Part of a group'}
            </div>
          )}
        </td>
        <td>
          <span className={`label ${stateDisplay.className}`}>{stateDisplay.label}</span>
        </td>
        <td>{item.warehouse?.name || '-'}</td>
        <td style={{ textAlign: 'center' }}>
          {selected && (
            <span
              style={{
                display: 'inline-block',
                minWidth: 20,
                padding: '2px 6px',
                borderRadius: 10,
                fontSize: 11,
                fontWeight: 600,
                background: actionCount > 0 ? TEAL : '#f0ad4e',
                color: '#fff',
              }}
            >
              {actionCount}
            </span>
          )}
        </td>
      </tr>
      {expanded && entry && (
        <tr>
          <td colSpan={8} style={{ padding: 0, background: '#fff' }}>
            <InlineActionPanel
              item={item}
              entry={entry}
              thumbnailSrc={thumbnailSrc}
              onToggleAction={onToggleAction}
              onSetNotes={onSetNotes}
            />
          </td>
        </tr>
      )}
    </>
  );
};

const InlineActionPanel: React.FC<{
  item: AccountItem;
  entry: SelectedItemEntry;
  thumbnailSrc?: string | null;
  onToggleAction: (itemId: string, actionType: WarehouseTask__ActionType) => void;
  onSetNotes: (itemId: string, actionType: WarehouseTask__ActionType, notes: string) => void;
}> = ({ item, entry, thumbnailSrc, onToggleAction, onSetNotes }) => (
  <div
    style={{
      borderTop: `2px solid ${TEAL}`,
      borderBottom: `1px solid ${TEAL_BORDER}`,
      background: '#fcfcfc',
    }}
  >
    <div style={{ display: 'flex', gap: 16, padding: '16px 20px' }}>
      <div style={{ flexShrink: 0, width: 80 }}>
        {thumbnailSrc ? (
          <img
            src={thumbnailSrc}
            alt={item.name || ''}
            style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }}
          />
        ) : (
          <div
            style={{
              width: 80,
              height: 80,
              background: '#eee',
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#999',
              fontSize: 11,
            }}
          >
            No photo
          </div>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ marginBottom: 12 }}>
          <strong style={{ fontSize: 14 }}>{item.name || item.contents || `Item #${item.id}`}</strong>
          <span className="text-muted" style={{ marginLeft: 8, fontSize: 12 }}>
            {[item.barcode?.value, item.category?.name, item.warehouse?.name, getStateDisplay(item.state).label]
              .filter(Boolean)
              .join(' \u00b7 ')}
          </span>
        </div>

        <div style={{ display: 'flex', gap: 32 }}>
          {Object.entries(ACTIONS_BY_GROUP).map(([group, actionTypes]) => (
            <div key={group} style={{ flex: 1, minWidth: 180 }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: TEAL,
                  marginBottom: 8,
                  paddingBottom: 4,
                  borderBottom: `1px solid ${TEAL_BORDER}`,
                }}
              >
                {ACTION_GROUP_LABELS[group]}
              </div>
              {actionTypes.map((at) => {
                const actionType = at as WarehouseTask__ActionType;
                const action = entry.actions.find((a) => a.actionType === actionType);
                const isSelected = !!action;
                return (
                  <div key={at} style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <label
                        style={{
                          fontWeight: 400,
                          fontSize: 13,
                          cursor: 'pointer',
                          margin: 0,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onToggleAction(entry.itemId, actionType)}
                          style={{ accentColor: TEAL }}
                        />
                        {ACTION_TYPE_LABELS[at] || at}
                      </label>
                      {isSelected && (
                        <input
                          type="text"
                          className="form-control input-sm"
                          placeholder="Add note..."
                          aria-label={`Notes for ${ACTION_TYPE_LABELS[actionType] || actionType}`}
                          value={action.notes}
                          onChange={(e) => onSetNotes(entry.itemId, actionType, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ flex: 1, minWidth: 120, maxWidth: 220 }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {entry.actions.length === 0 && (
          <p style={{ fontSize: 12, margin: '8px 0 0', color: '#f0ad4e' }}>Select at least one action for this item</p>
        )}
      </div>
    </div>
  </div>
);
